import { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Image,
  Upload,
  Search,
  X,
  FileImage,
  BarChart3,
  LineChart,
  PieChart,
  Microscope,
  Camera,
  Sparkles,
  Download,
  BookOpen,
  ExternalLink,
  ImagePlus,
  Zap,
  Eye,
  Info,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { ImageSearchResult } from '@/types/research';
import { cn } from '@/lib/utils';

const MATCH_TYPE_CONFIG: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  chart: { icon: <BarChart3 className="h-4 w-4" />, label: 'Chart', color: 'bg-blue-500' },
  diagram: { icon: <PieChart className="h-4 w-4" />, label: 'Diagram', color: 'bg-purple-500' },
  photo: { icon: <Camera className="h-4 w-4" />, label: 'Photo', color: 'bg-green-500' },
  illustration: { icon: <Image className="h-4 w-4" />, label: 'Illustration', color: 'bg-orange-500' },
};

// Sample results for demonstration
const SAMPLE_RESULTS: ImageSearchResult[] = [
  {
    resourceId: 'res-001',
    resourceTitle: 'Climate Change Impact on Great Barrier Reef Coral Bleaching Patterns 2020-2024',
    imageUrl: '/images/reef-temperature-chart.png',
    thumbnailUrl: 'https://placehold.co/300x200/667eea/white?text=Temperature+Chart',
    similarity: 0.94,
    matchType: 'chart',
  },
  {
    resourceId: 'res-006',
    resourceTitle: 'Australian Urban Heat Island Effect: Multi-City Temperature Analysis',
    imageUrl: '/images/heat-map-sydney.png',
    thumbnailUrl: 'https://placehold.co/300x200/48bb78/white?text=Heat+Map',
    similarity: 0.87,
    matchType: 'diagram',
  },
  {
    resourceId: 'res-012',
    resourceTitle: 'Antarctic Ice Core Data: 800,000 Year Climate Record',
    imageUrl: '/images/co2-timeline.png',
    thumbnailUrl: 'https://placehold.co/300x200/764ba2/white?text=CO2+Timeline',
    similarity: 0.82,
    matchType: 'chart',
  },
  {
    resourceId: 'res-008',
    resourceTitle: 'Renewable Energy Grid Integration: South Australia Case Study',
    imageUrl: '/images/energy-flow-diagram.png',
    thumbnailUrl: 'https://placehold.co/300x200/ed8936/white?text=Energy+Flow',
    similarity: 0.76,
    matchType: 'diagram',
  },
  {
    resourceId: 'res-010',
    resourceTitle: 'High-Resolution Electron Microscopy Images: Australian Native Bee Species',
    imageUrl: '/images/bee-microscopy.png',
    thumbnailUrl: 'https://placehold.co/300x200/f687b3/white?text=Bee+SEM',
    similarity: 0.71,
    matchType: 'photo',
  },
];

export function ResearchImageSearchPage() {
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [results, setResults] = useState<ImageSearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<ImageSearchResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      handleImageUpload(files[0]);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0]);
    }
  }, []);

  const handleImageUpload = (file: File) => {
    setUploadedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    setResults([]);
  };

  const clearImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    setResults([]);
  };

  const handleSearch = async () => {
    if (!uploadedImage) return;

    setIsSearching(true);
    setSearchProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setSearchProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 150);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSearchProgress(100);
      setResults(SAMPLE_RESULTS);
    } finally {
      clearInterval(progressInterval);
      setIsSearching(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Visual Content Search</h1>
          <p className="text-muted-foreground">
            Upload a chart, diagram, or image to find similar research across the platform
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-pink-500/5 to-purple-500/5 border-pink-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 text-white">
              <Sparkles className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">AI-Powered Visual Search</h3>
              <p className="text-muted-foreground mt-1">
                Our advanced computer vision AI analyzes your uploaded images to find visually similar
                charts, diagrams, and experimental images across thousands of research resources.
                Perfect for finding similar methodologies, experimental designs, or data visualizations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImagePlus className="h-5 w-5" />
              Upload Image
            </CardTitle>
            <CardDescription>
              Drag and drop or click to upload a chart, graph, or diagram
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!imagePreview ? (
              <div
                className={cn(
                  'border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer',
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">
                  {isDragging ? 'Drop your image here' : 'Upload an image'}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Supports PNG, JPG, JPEG, GIF up to 10MB
                </p>
                <div className="flex justify-center gap-4 mt-6">
                  <Badge variant="secondary" className="gap-1">
                    <BarChart3 className="h-3 w-3" />
                    Charts
                  </Badge>
                  <Badge variant="secondary" className="gap-1">
                    <PieChart className="h-3 w-3" />
                    Diagrams
                  </Badge>
                  <Badge variant="secondary" className="gap-1">
                    <Microscope className="h-3 w-3" />
                    Microscopy
                  </Badge>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative rounded-xl overflow-hidden border">
                  <img
                    src={imagePreview}
                    alt="Uploaded image"
                    className="w-full h-auto max-h-[400px] object-contain bg-muted"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={clearImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium truncate">{uploadedImage?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {((uploadedImage?.size || 0) / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button onClick={handleSearch} disabled={isSearching}>
                    {isSearching ? (
                      <>
                        <Zap className="mr-2 h-4 w-4 animate-pulse" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-4 w-4" />
                        Find Similar
                      </>
                    )}
                  </Button>
                </div>
                {isSearching && (
                  <div className="space-y-2">
                    <Progress value={searchProgress} className="h-2" />
                    <p className="text-sm text-muted-foreground text-center">
                      Analyzing visual features...
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              How It Works
            </CardTitle>
            <CardDescription>
              Visual search process explained
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-semibold">Upload Your Image</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload a chart, diagram, graph, or any visual from your research
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-semibold">AI Feature Extraction</h4>
                  <p className="text-sm text-muted-foreground">
                    Our AI analyzes visual patterns, colors, shapes, and structural elements
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-semibold">Similarity Matching</h4>
                  <p className="text-sm text-muted-foreground">
                    Compare against millions of research images using vector similarity
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-bold flex-shrink-0">
                  4
                </div>
                <div>
                  <h4 className="font-semibold">Get Results</h4>
                  <p className="text-sm text-muted-foreground">
                    Discover similar research with detailed similarity scores
                  </p>
                </div>
              </div>
            </div>

            {/* Example Use Cases */}
            <div className="mt-8 pt-6 border-t">
              <h4 className="font-semibold mb-4">Example Use Cases</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <LineChart className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">
                    Upload a temperature trend chart to find related climate studies
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Microscope className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">
                    Share microscopy images to find similar biological structures
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <PieChart className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">
                    Find papers using similar data visualization techniques
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Results
            </CardTitle>
            <CardDescription>
              Found {results.length} similar images across research resources
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {results.map((result) => {
                const matchConfig = MATCH_TYPE_CONFIG[result.matchType];

                return (
                  <Card
                    key={result.resourceId}
                    className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50 overflow-hidden"
                    onClick={() => setSelectedResult(result)}
                  >
                    <div className="relative aspect-video bg-muted">
                      <img
                        src={result.thumbnailUrl}
                        alt={result.resourceTitle}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2">
                        <Badge
                          className={cn(
                            'text-white border-0',
                            result.similarity >= 0.9
                              ? 'bg-green-500'
                              : result.similarity >= 0.8
                              ? 'bg-blue-500'
                              : 'bg-orange-500'
                          )}
                        >
                          {Math.round(result.similarity * 100)}% match
                        </Badge>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="gap-1">
                          {matchConfig.icon}
                          {matchConfig.label}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-medium line-clamp-2 text-sm">
                        {result.resourceTitle}
                      </h4>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result Detail Dialog */}
      <Dialog open={!!selectedResult} onOpenChange={() => setSelectedResult(null)}>
        <DialogContent className="max-w-2xl">
          {selectedResult && (
            <>
              <DialogHeader>
                <DialogTitle>Similar Resource Found</DialogTitle>
                <DialogDescription>
                  {Math.round(selectedResult.similarity * 100)}% visual similarity match
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                  <img
                    src={selectedResult.thumbnailUrl}
                    alt={selectedResult.resourceTitle}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-lg">{selectedResult.resourceTitle}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={cn('text-white border-0', MATCH_TYPE_CONFIG[selectedResult.matchType].color)}>
                      {MATCH_TYPE_CONFIG[selectedResult.matchType].icon}
                      <span className="ml-1">{MATCH_TYPE_CONFIG[selectedResult.matchType].label}</span>
                    </Badge>
                    <Badge
                      variant="outline"
                      className={cn(
                        selectedResult.similarity >= 0.9
                          ? 'border-green-500 text-green-600'
                          : selectedResult.similarity >= 0.8
                          ? 'border-blue-500 text-blue-600'
                          : 'border-orange-500 text-orange-600'
                      )}
                    >
                      {Math.round(selectedResult.similarity * 100)}% similarity
                    </Badge>
                  </div>
                </div>

                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <h4 className="font-medium mb-2">Why this match?</h4>
                    <p className="text-sm text-muted-foreground">
                      This resource was matched based on visual similarity analysis including:
                      chart structure, color patterns, data visualization style, and layout composition.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedResult(null)}>
                  Close
                </Button>
                <Button variant="outline">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Generate Citation
                </Button>
                <Button>
                  <Eye className="mr-2 h-4 w-4" />
                  View Resource
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
